/** @jsxImportSource @emotion/react */
import * as d3 from 'd3'
import { useCallback, useEffect, useRef, useState } from 'react'
import { roundForDisplay } from './utils/utils'
import { Box, useTheme } from '@mui/material'
import { Circle, ErrorOutline } from '@mui/icons-material'
import { ChartTooltip } from './ChartTooltip'
import { useAtomValue } from 'jotai'
import { currencySymbolAtom } from '@app/state/clientsAtom'

export const MakeGaugeSvg = (
  element: HTMLElement,
  value: number,
  total: number,
  currencySymbol: string,
  unit?: string,
  prefix = '',
  equalOrGreaterThan = false,
) => {
  // set the dimensions and margins of the graph
  const fullWidth = 200
  const fullHeight = 130
  const margin = { top: 10, right: 10, bottom: 10, left: 10 }
  const width = fullWidth - margin.left - margin.right

  let displayPercentage = ''
  let gaugePos = 0
  let greaterThanPrefix = ''
  if (value == 0) displayPercentage = '0%'
  else {
    const proportion = value / total
    gaugePos = proportion * 180 + 6
    const percentage = Math.round(proportion * 100)
    if (proportion < 0.01) displayPercentage = '<1%'
    else {
      greaterThanPrefix = equalOrGreaterThan && percentage < 100 ? '>' : ''
      displayPercentage = `${greaterThanPrefix}${percentage}%`
    }
  }

  d3.select(element).selectAll('svg').remove()

  const svg = d3
    .select(element)
    .append('svg')
    .attr('version', '1.1')
    .attr('xmlns', 'http://www.w3.org/2000/svg')
    .attr('width', fullWidth)
    .attr('height', fullHeight)
    .attr('viewBox', `0 0 ${fullWidth} ${fullHeight}`)
    .attr('color', 'black')
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  const radius = Math.floor(width / 2.4)
  const thickness = radius * 0.25
  const arc = d3.arc().cornerRadius(thickness / 2)
  const body = svg.append('g').attr('transform', `translate(${width / 2},${radius})`)

  const gaugeBody = body.append('g').attr('transform', `rotate(-90)`)
  gaugeBody
    .append('path')
    .attr(
      'd',
      arc({
        startAngle: deg2rad(-8),
        endAngle: deg2rad(188),
        outerRadius: radius,
        innerRadius: radius - thickness,
      }),
    )
    .attr('fill', '#e0e4e8')

  gaugeBody
    .append('path')
    .attr(
      'd',
      arc({
        startAngle: deg2rad(-6),
        endAngle: deg2rad(gaugePos),
        outerRadius: radius - 2,
        innerRadius: radius - thickness + 2,
      }),
    )
    .attr('fill', '#c43424')

  body
    .append('text')
    .text(displayPercentage)
    .attr('x', 0)
    .attr('y', 0)
    .attr('style', 'text-anchor: middle; font-size: 24pt; font-weight: 800; fill: #0b2948;')

  const formattedValue =
    prefix === currencySymbol ? roundForDisplay(value, '$') : roundForDisplay(value, unit)
  const formattedTotal =
    prefix === currencySymbol ? roundForDisplay(total, '$') : roundForDisplay(total, unit)
  const caption1 = `${greaterThanPrefix}${prefix}${formattedValue.toLocaleString()} out of`
  const caption2 = `${prefix}${formattedTotal.toLocaleString()} ${unit}`
  body
    .append('text')
    .attr('x', 0)
    .attr('y', 24)
    .attr('style', 'text-anchor: middle; font-size: 8pt; fill: #8392a1;')
    .text(caption1)
  body
    .append('text')
    .attr('x', 0)
    .attr('y', 36)
    .attr('style', 'text-anchor: middle; font-size: 8pt; fill: #8392a1;')
    .text(caption2)
}

interface GaugeProps {
  value: number
  total: number
  unit?: string
  prefix?: string
  equalOrGreaterThan?: boolean
  uncertainty?: { is_uncertain: boolean; tooltip: string }
}
export const Gauge = ({
  value,
  total,
  unit,
  prefix,
  equalOrGreaterThan,
  uncertainty,
}: GaugeProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipText, setTooltipText] = useState('')
  const [tooltipColor, setTooltipColor] = useState('')
  const currencySymbol = useAtomValue(currencySymbolAtom)

  const updateTooltip = useCallback(
    ({ show, text, color }: { show: boolean; text?: string; color?: string }) => {
      setShowTooltip(show)
      if (color) setTooltipColor(color)
      if (text) setTooltipText(text)
    },
    [],
  )

  useEffect(() => {
    if (!ref.current) return
    MakeGaugeSvg(ref.current, value, total, currencySymbol, unit, prefix, equalOrGreaterThan)
  }, [value, total, unit, prefix, equalOrGreaterThan, currencySymbol])

  return (
    <ChartTooltip show={showTooltip} items={[{ text: tooltipText, color: tooltipColor }]}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div ref={ref}></div>
        {uncertainty?.is_uncertain && (
          <Box
            css={{
              position: 'absolute',
              width: '24px',
              height: '24px',
              flexShrink: 0,
              right: '14px',
              transform: 'translateY(6px)',
            }}
            onMouseOver={() =>
              updateTooltip({ show: true, text: uncertainty?.tooltip, color: '#d9d9d9' })
            }
            onMouseOut={() => updateTooltip({ show: false })}
          >
            <Circle
              style={{
                width: '100%',
                height: '100%',
                color: 'white',
                position: 'absolute',
                top: '0px',
                left: '0px',
              }}
            />
            <ErrorOutline
              style={{
                width: '100%',
                height: '100%',
                color: theme.palette.uintel.main,
                position: 'absolute',
                top: '0px',
                left: '0px',
              }}
            />
          </Box>
        )}
      </Box>
    </ChartTooltip>
  )
}

function deg2rad(deg: number): number {
  return (Math.PI / 180.0) * deg
}
